/**
 * Delivery of a public lead form straight to the MergeXAI CRM.
 *
 * The CRM is the system of record for leads: nothing here writes to Supabase.
 * The inbound-webhook workflow on the other end creates the contact, assigns it
 * to Andrew Bullio, and opens an opportunity in Bullio Sales at the New Lead
 * stage.
 *
 * Every public form goes through this one module so the two of them cannot
 * drift apart in the CRM. A field that only one form sends is a field the
 * workflow cannot rely on, so the wire format is built here, in one place,
 * from the semantic values a page actually has.
 */

/**
 * The endpoint is the same for every form; `form_name` is what tells them
 * apart on the CRM side. The override exists so a staging build can point at a
 * test webhook without a code change.
 */
const CRM_WEBHOOK_URL =
  import.meta.env.VITE_CRM_WEBHOOK_URL ??
  "https://services.leadconnectorhq.com/hooks/bKo60DtcFNC9UkysavTn/webhook-trigger/178cbb04-2edf-4991-a938-ac977e922f44";

/** The value of `form_name`, which is how the CRM tells the two forms apart. */
export type CrmFormName = "lp" | "site";

export type CrmLeadPayload = {
  /**
   * As typed, each field its own. The forms used to ask for one "full name"
   * and this module guessed the split on whitespace, which gets "Mary Jane
   * Watson" wrong — first "Mary", last "Jane Watson" — and leaves a
   * one-word answer with no surname at all. The CRM has always had two
   * fields; now so does every form, so nothing is being guessed.
   */
  first_name: string;
  last_name: string;
  email: string;
  /** `/enroll` has no phone field at all, so it sends nothing. */
  phone?: string;
  /** The enum value, never the label shown to the visitor. */
  experience_level: string;
  /** Only `/enroll` asks this. */
  goals?: string;
  /**
   * Where the lead came from, in prose, for a human reading the CRM. Built by
   * the calling page because only it knows what to call itself.
   */
  attribution: string;
  form_name: CrmFormName;
  /** The path the form sits on, e.g. `/lp`. */
  landing_page: string;
  /**
   * `useLocation().search` from the calling page — not `window.location`,
   * which is a step behind the router on a client-side navigation and would
   * attribute a lead to the wrong campaign.
   */
  search: string;
  /**
   * Seconds between the page opening and the form being sent, so the CRM can
   * sort a queue by it. This is what replaced the hidden bot trap: the trap
   * caught password managers as readily as bots and refused real people, and
   * on a page paid for by the click a lead that vanishes is worse than a spam
   * lead somebody deletes. Nothing here refuses a submission — the number is
   * only a number, for a human to judge.
   */
  fillSeconds?: number;
};

/**
 * Builds the form-encoded body sent to the webhook.
 *
 * The key order is the one /lp has been sending in production since the CRM
 * switch. Form encoding does not give order any meaning, but keeping it makes
 * this refactor a one-field change against a live integration rather than a
 * rewrite of its wire format, and `goals` is that one field.
 *
 * Exported for the tests, which assert on the exact format: this is a contract
 * with a system outside this repository, so a change to it has to be a
 * deliberate one that updates a test.
 */
export function buildCrmLeadBody(payload: CrmLeadPayload): URLSearchParams {
  const params = new URLSearchParams(payload.search);
  // Campaign parameters are sent even when absent, as empty values: a workflow
  // that maps a field is simpler to reason about when the field always exists.
  const utm = (key: string) => params.get(key) ?? "";
  // Two fields on every form and two in the CRM. `full_name` is still sent,
  // joined from them, because the workflow on the other end reads it.
  const firstName = payload.first_name.trim().slice(0, 50);
  const lastName = payload.last_name.trim().slice(0, 50);
  // Capped at what the CRM field holds: fifty each joined by a space is 101.
  const fullName = `${firstName} ${lastName}`.trim().slice(0, 100);

  return new URLSearchParams({
    full_name: fullName,
    first_name: firstName,
    last_name: lastName,
    email: payload.email.trim().slice(0, 255),
    phone: (payload.phone ?? "").trim().slice(0, 20),
    experience_level: payload.experience_level,
    // Only /enroll asks this; /lp sends it empty so both forms map the same.
    goals: (payload.goals ?? "").trim().slice(0, 2000),
    heard_from: payload.attribution,
    attribution: payload.attribution,
    source: "website",
    form_name: payload.form_name,
    landing_page: payload.landing_page,
    page_url: window.location.href,
    // Appended, not inserted: the fields above are the order this integration
    // has been receiving since the CRM switch, and a new one belongs at the end.
    fill_seconds: String(payload.fillSeconds ?? ""),
    utm_source: utm("utm_source"),
    utm_medium: utm("utm_medium"),
    utm_campaign: utm("utm_campaign"),
    utm_term: utm("utm_term"),
    utm_content: utm("utm_content"),
    submitted_at: new Date().toISOString(),
  });
}

/**
 * Whether a delivery arrived, and if not, why.
 *
 * One shape rather than a union discriminated on `delivered`: this project
 * compiles with `strict: false`, and without `strictNullChecks` TypeScript
 * does not narrow a union on a boolean literal, so `if (!result.delivered)`
 * would leave `reason` unreachable at every call site.
 */
export type CrmDeliveryResult = {
  delivered: boolean;
  /** Set when it arrived: whether the response could be read, or was opaque. */
  via?: "cors" | "opaque";
  /** Set when it did not, e.g. `crm-http-404`. For a log, not for a visitor. */
  reason?: string;
};

/**
 * Sends a lead to the CRM. Reports whether it actually arrived, and if not,
 * why.
 *
 * The body is form-encoded on purpose. That keeps this a CORS "simple request"
 * with no preflight; an `application/json` content type triggers an OPTIONS
 * that this endpoint does not answer, and the lead never leaves the page. This
 * has been tested against the live endpoint — do not "improve" it into JSON.
 *
 * Two paths, because a webhook endpoint is a server-to-server thing and is
 * under no obligation to answer a browser. If it does not return
 * `Access-Control-Allow-Origin`, the request still leaves the browser and the
 * CRM still receives it, but the browser refuses to hand the response back to
 * this code and `fetch` rejects. Read literally that is indistinguishable from
 * "nothing was sent", and it is why a visitor could be told their details did
 * not go through while the lead was sitting in the CRM.
 *
 * So a rejection is retried opaquely. `mode: "no-cors"` cannot read the
 * response — `ok` is always false and `status` is always 0 — but it resolves
 * only if the browser reached the endpoint and got an answer, which is the
 * evidence the first attempt was denied. The cost is that a lead the endpoint
 * rejected with a 500 now looks delivered, and that a CORS-blocked first
 * attempt is sent twice. Both are better than the alternative on a page that
 * is paid for by the click: a duplicate contact is a nuisance, a lead nobody
 * knows arrived is the whole spend wasted.
 */
export async function deliverLeadToCrm(payload: CrmLeadPayload): Promise<CrmDeliveryResult> {
  const body = buildCrmLeadBody(payload);

  try {
    const res = await fetch(CRM_WEBHOOK_URL, { method: "POST", body });
    if (res.ok) return { delivered: true, via: "cors" };
    // A real answer with a bad status. Nothing to retry: the endpoint read
    // the request and refused it, and sending it again would only repeat it.
    return { delivered: false, reason: `crm-http-${res.status}` };
  } catch (err) {
    // Either the endpoint gave the browser no CORS header, or the network is
    // gone. The two look identical from here, so try the one request that can
    // tell them apart.
    console.error("[crmLead] direct POST rejected, retrying opaque", err);
    try {
      await fetch(CRM_WEBHOOK_URL, { method: "POST", body, mode: "no-cors" });
      return { delivered: true, via: "opaque" };
    } catch (opaqueErr) {
      console.error("[crmLead] opaque POST failed too", opaqueErr);
      return {
        delivered: false,
        reason: `crm-unreachable: ${opaqueErr instanceof Error ? opaqueErr.message : String(opaqueErr)}`,
      };
    }
  }
}

/** The old shape, for callers that only need the yes/no. */
export async function submitLeadToCrm(payload: CrmLeadPayload): Promise<boolean> {
  return (await deliverLeadToCrm(payload)).delivered;
}

/**
 * The prose attribution stamped on the lead, e.g.
 * `Landing Page /lp · utm_source=google`.
 *
 * Without it a campaign lead is indistinguishable from an organic one in the
 * CRM, which makes the ad spend impossible to judge. Capped at 200 characters,
 * which is what the field behind it holds.
 */
export function buildAttribution(label: string, search: string): string {
  const params = new URLSearchParams(search);
  const parts = [label];
  for (const key of ["utm_source", "utm_medium", "utm_campaign"]) {
    const value = params.get(key);
    if (value) parts.push(`${key}=${value.slice(0, 50)}`);
  }
  return parts.join(" · ").slice(0, 200);
}
